// Schicht-Logik — geteilt zwischen Kapazitätsplanung, Schichtansicht und der
// Schicht-Erfassung. Reine Funktionen, keine Vue-Abhängigkeit.
//
// Vorgabe 26.06.2026 (260626_BDE.md #16, „neu präzisiert“):
//   1-Schicht-Betrieb : 8 h, 07:00–15:30
//   2-Schicht-Betrieb : 06:00–14:00 (S1) und 14:00–24:00 (S2)
// Die Zeitfenster der beiden Betriebsarten sind NICHT dieselben — im
// 1-Schicht-Betrieb beginnt die Schicht eine Stunde später und endet 15:30.
//
// Spiegelt backend/app/models/setting.rb. Bei einer Änderung BEIDE anfassen.

// Eine Schicht = 8 h Arbeitszeit. Nenner jeder Auslastungsanzeige.
export const SHIFT_HOURS = 8

// Obergrenze je Schicht (08.06.2026 / 16.06.2026 #3): mehr als 6 Aufträge
// passen weder auf die Maschine noch lesbar in die Zelle.
export const MAX_JOBS_PER_SHIFT = 6

// 2-Schicht-Betrieb.
export const SHIFTS_DUAL = [
  { nr: 1, label: 'Frühschicht', kurz: 'S1', zeit: '06–14', von: 6,  bis: 14 },
  { nr: 2, label: 'Spätschicht', kurz: 'S2', zeit: '14–24', von: 14, bis: 24 }
]

// 1-Schicht-Betrieb: ein Fenster 07:00–15:30 (8 h + 30 min Pause).
export const SHIFTS_SINGLE = [
  { nr: 1, label: 'Schicht', kurz: 'S', zeit: '07:00–15:30', von: 7, bis: 15.5 }
]

// Welche Schichten gelten bei diesem Betriebsmodus?
export function activeShifts(shiftSystem) {
  return Number(shiftSystem) === 1 ? SHIFTS_SINGLE.slice() : SHIFTS_DUAL.slice()
}

export function shiftDef(nr, shiftSystem = 2) {
  return activeShifts(shiftSystem).find(s => s.nr === Number(nr)) || null
}

// Sichtbarer Name einer Schicht.
export function shiftLabel(nr, shiftSystem = 2) {
  const s = shiftDef(nr, shiftSystem)
  return s ? `${s.label} (${s.zeit})` : 'ohne Schicht'
}

// Uhrzeit (Dezimalstunde) → Schichtnummer für den gegebenen Betriebsmodus.
//
// 2-Schicht: 06:00–13:59 → 1 · 14:00–23:59 → 2 · 00:00–05:59 → 2 (Ausklang
//            der Spätschicht, die bis 24 Uhr angesetzt ist).
// 1-Schicht: 07:00–15:29 → 1 · alles andere → null (außerhalb der Schicht).
export function shiftForHour(hour, shiftSystem = 2) {
  const h = Number(hour)
  if (!isFinite(h)) return null

  if (Number(shiftSystem) === 1) {
    const s = SHIFTS_SINGLE[0]
    return h >= s.von && h < s.bis ? 1 : null
  }

  return h >= SHIFTS_DUAL[0].von && h < SHIFTS_DUAL[0].bis ? 1 : 2
}

// Date | ISO-String mit Uhrzeit → Schichtnummer, sonst null.
export function shiftForTime(value, shiftSystem = 2) {
  if (!value) return null
  const d = value instanceof Date ? value : new Date(value)
  if (isNaN(d.getTime())) return null
  return shiftForHour(d.getHours() + d.getMinutes() / 60, shiftSystem)
}

export function currentShift(now = new Date(), shiftSystem = 2) {
  return shiftForTime(now, shiftSystem)
}

// Die Schicht EINER Maschinen-Zuordnung. Ohne Uhrzeit im Planfenster lässt
// sich keine Schicht ableiten — dann `null` (= „noch nicht eingeteilt“), damit
// die Karte nicht willkürlich in Schicht 1 landet.
export function shiftForAssignment(assignment, shiftSystem = 2) {
  if (!assignment || !assignment.geplant_start) return null
  // Ein reines Datum ("2026-09-05") trägt keine Uhrzeit → keine Schicht.
  if (!/\d{1,2}:\d{2}/.test(String(assignment.geplant_start))) return null
  return shiftForTime(assignment.geplant_start, shiftSystem)
}

// Kapazität eines Tages in Stunden: 8 h bei 1-Schicht, 16 h bei 2-Schicht.
export function dailyCapacityHours(shiftSystem) {
  return activeShifts(shiftSystem).length * SHIFT_HOURS
}

// Auslastung einer Schicht in Prozent (kann >100 sein — das ist die Aussage).
export function loadPct(hours, capacityHours = SHIFT_HOURS) {
  const cap = capacityHours > 0 ? capacityHours : SHIFT_HOURS
  return Math.round(((Number(hours) || 0) / cap) * 100)
}

// Ampel für die Auslastung. Der Chef soll die Farbe lesen, nicht die Zahl.
//   frei   < 60 %   grün
//   voll   60–100 % gelb
//   über   > 100 %  rot
export function loadLevel(hours, capacityHours = SHIFT_HOURS) {
  const pct = loadPct(hours, capacityHours)
  if (pct > 100) return 'over'
  if (pct >= 60) return 'full'
  return 'free'
}

export const LOAD_LABEL = {
  free: 'Luft',
  full: 'gut ausgelastet',
  over: 'überbucht'
}

// Mehr Aufträge als in eine Schicht passen (Vorgabe: max. 6).
export function isOverbooked(jobCount) {
  return Number(jobCount) > MAX_JOBS_PER_SHIFT
}

// px pro Stunde, damit eine 8-h-Schicht genau eine Maschinenzeile hoch ist.
// Vorgabe 16.06. #3: „Höhe relativ zur Screenhöhe der 4 Maschinen“ — die
// Referenz sind VIER sichtbare Maschinen, nicht alle Lanes. Ohne Messwert
// (erster Paint) bleibt es beim bisherigen Festwert.
export const HOUR_PX_FALLBACK = 26
export const HOUR_PX_MIN = 12
export const HOUR_PX_MAX = 44
export const VIEWPORT_REFERENCE_MACHINES = 4

export function hourPxFor(panelHeightPx, referenceMachines = VIEWPORT_REFERENCE_MACHINES) {
  const h = Number(panelHeightPx)
  const n = Number(referenceMachines) || VIEWPORT_REFERENCE_MACHINES
  if (!isFinite(h) || h <= 0) return HOUR_PX_FALLBACK
  const perHour = (h / n) / SHIFT_HOURS
  return Math.round(Math.min(HOUR_PX_MAX, Math.max(HOUR_PX_MIN, perHour)))
}

// ── Überläufer (08.06.2026.md „Überläufer“) ─────────────────────────────────
// „Wenn Gesamtzeit > 8h: automatische Darstellung in Folgeschicht/Folgetag mit
//  Höhe = Reststundenzahl“ · „Min.-Höhe 1h“.
//
// Verteilt die Stunden, die NICHT mehr in den ersten Tag passen, auf die
// folgenden Tage: je Tag höchstens `thresholdHours`, jeder Block mindestens
// 1 h. Gibt die Blockhöhen in Stunden zurück, einer je Folgetag.
//
//   distributeOverflow(12, 8)      -> [4]          ein Folgetag mit 4 h
//   distributeOverflow(20, 8)      -> [8, 4]       zwei Folgetage
//   distributeOverflow(8.25, 8)    -> [1]          Rest 0,25 h -> Mindesthöhe 1 h
//   distributeOverflow(6, 8)       -> []           kein Überlauf
export const OVERFLOW_MIN_BLOCK_HOURS = 1

// `thresholdHours` = was am ERSTEN Tag noch Platz hat, `nextDayHours` = was
// jeder Folgetag fassen kann (Default: dasselbe wie der erste Tag).
export function distributeOverflow(totalHours, thresholdHours = SHIFT_HOURS, maxBlocks = 14, nextDayHours = thresholdHours) {
  const total = Number(totalHours)
  const first = Number(thresholdHours)
  const step = Number(nextDayHours)
  if (!isFinite(total) || !isFinite(first) || first < 0) return []
  if (!isFinite(step) || step <= 0) return []
  if (!(total > first)) return []

  const blocks = []
  let remaining = total - first
  // 0.001 statt 0: Gleitkomma-Reste wie 1e-15 sollen keinen Extra-Block geben.
  while (remaining > 0.001 && blocks.length < maxBlocks) {
    const chunk = Math.min(remaining, step)
    blocks.push(Math.max(OVERFLOW_MIN_BLOCK_HOURS, chunk))
    remaining -= chunk
  }
  return blocks
}

// Wie viele Schichtstunden hat der Tag ab diesem Startzeitpunkt noch?
//
// Chef-Entscheid 17.09.2026: ein langer Auftrag läuft erst dann auf den
// Folgetag über, wenn er auch in der SPÄTSCHICHT keinen Platz mehr hat. Es
// zählt also, was vom Tag ab dem Start noch übrig ist — nicht pauschal 8 h.
//
// Jede Schicht ist 8 h wert. Startet der Auftrag mitten in einer Schicht,
// ist deren bereits verstrichene Zeit weg; spätere Schichten zählen voll.
//
//   2-Schicht, Start 06:00 → 8 + 8 = 16 h
//   2-Schicht, Start 07:30 → 6,5 + 8 = 14,5 h
//   2-Schicht, Start 14:00 → 8 h  (nur noch die Spätschicht)
//   2-Schicht, Start 20:00 → 2 h
//   1-Schicht, Start 07:00 → 8 h
//
// Ohne Uhrzeit weiß man nichts über die Lage im Tag → volle Tageskapazität.
export function hoursLeftInDay(start, shiftSystem = 2) {
  const cap = dailyCapacityHours(shiftSystem)
  if (!start) return cap
  const str = String(start)
  if (!(start instanceof Date) && !/\d{1,2}:\d{2}/.test(str)) return cap
  const d = start instanceof Date ? start : new Date(str)
  if (isNaN(d.getTime())) return cap
  const hour = d.getHours() + d.getMinutes() / 60

  let left = 0
  for (const s of activeShifts(shiftSystem)) {
    if (hour >= s.bis) continue                       // Schicht schon vorbei
    const elapsed = Math.max(0, hour - s.von)         // vor Schichtbeginn: 0
    left += Math.max(0, SHIFT_HOURS - elapsed)
  }
  return left
}
